import type { PxAnswer, PxQuestion } from '~/meta/data/px-connect/types';
import { assignAnswerUids } from '~/meta/data/px-connect/assignAnswerUids';
import type { PxDraftAnswer } from '~/meta/data/px-connect/draftAnswers';

/**
 * State + reducer for the PX Connect Schadenbericht form (UCPWO-378).
 *
 * State is the fetched `PxQuestion[]` itself — the single source of truth. The
 * reducer fills in `isSelected` / `comment` / `quantity` on answer objects; the
 * submit DTO is the same array shape. See docs/adr/0004.
 *
 * Identity is the `(questionKey, uid)` pair, where `uid` is an internal handle
 * assigned on `INIT` (see `assignAnswerUids`). The API's `answer.key` is NOT
 * guaranteed unique within a question (e.g. DEBEKA's `DBK_GE_5`), so we never key
 * off `key` — and we leave `key` untouched so it can be submitted back verbatim.
 *
 * Visibility is derived (not stored): a question shows if it is root-level
 * (`parentKey === ''`) or referenced by a currently-selected answer's
 * `relatedQuestions`. After every selection change the state is normalized via
 * `clearHiddenQuestions` — any question no longer visible has its answers
 * cleared. A question revealed by several answers therefore survives as long as
 * any of them stays selected, and no stale hidden answers survive.
 */

export type PxReportState = PxQuestion[];

export type PxReportAction =
    | { type: 'INIT'; questions: PxQuestion[] }
    | { type: 'APPLY_DRAFT'; answers: PxDraftAnswer[] }
    | { type: 'SELECT_ANSWER'; questionKey: string; answerUid: string }
    | { type: 'SET_COMMENT'; questionKey: string; answerUid: string; comment: string }
    | { type: 'SET_QUANTITY'; questionKey: string; answerUid: string; quantity: number };

const buildQuestionMap = (questions: PxReportState): Record<string, PxQuestion> => {
    const map: Record<string, PxQuestion> = {};
    for (const question of questions) map[question.key] = question;
    return map;
};

const findAnswer = (question: PxQuestion | undefined, uid: string): PxAnswer | undefined =>
    question?.answers.find((answer) => answer.uid === uid);

/** Clear an answer's own fill-in fields. */
const clearAnswerFields = (answer: PxAnswer): void => {
    answer.isSelected = false;
    answer.comment = null;
    answer.quantity = 0;
};

/** Keys of questions currently visible — same rule as collectVisibleQuestions. */
const computeVisibleKeys = (questions: PxReportState): Set<string> => {
    const byKey = buildQuestionMap(questions);
    const visible = new Set<string>();
    const visit = (question: PxQuestion): void => {
        if (visible.has(question.key)) return;
        visible.add(question.key);
        for (const answer of question.answers) {
            if (!answer.isSelected) continue;
            for (const relatedKey of answer.relatedQuestions || []) {
                const related = byKey[relatedKey];
                if (related) visit(related);
            }
        }
    };
    questions.filter((question) => !question.parentKey).forEach(visit);
    return visible;
};

/**
 * Clear every question that is no longer visible. Replaces the former
 * answer-keyed recursive reset: a question revealed by several answers survives
 * as long as ANY of them is still selected (e.g. SC_1's answers
 * LE_1/GR_1/OB_84/KE_1 all reveal LE_2 in the shipped catalogs, so switching
 * between them must not wipe LE_2). One pass suffices: visibility is defined
 * transitively from the roots, so selections on already-invisible questions
 * cannot make anything visible.
 */
const clearHiddenQuestions = (questions: PxReportState): void => {
    const visible = computeVisibleKeys(questions);
    for (const question of questions) {
        if (visible.has(question.key)) continue;
        for (const answer of question.answers) {
            if (answer.isSelected || answer.comment !== null || answer.quantity !== 0) {
                clearAnswerFields(answer);
            }
        }
    }
};

export const pxReportReducer = (
    state: PxReportState,
    action: PxReportAction
): PxReportState => {
    switch (action.type) {
        case 'INIT': {
            // Assign internal uids so identity never relies on the API key, which
            // is not guaranteed unique within a question (see assignAnswerUids).
            return assignAnswerUids(structuredClone(action.questions));
        }

        case 'APPLY_DRAFT': {
            // Re-applies a compact saved draft (questionKey/answerUid + value) onto a
            // freshly INIT'd catalog — see extractDraftAnswers for why we don't
            // persist/restore the full PxQuestion[] tree.
            const next = structuredClone(state);
            const map = buildQuestionMap(next);
            for (const saved of action.answers) {
                const answer = findAnswer(map[saved.questionKey], saved.answerUid);
                if (!answer) continue;
                answer.isSelected = saved.isSelected;
                answer.comment = saved.comment;
                answer.quantity = saved.quantity;
            }
            clearHiddenQuestions(next);
            return next;
        }

        case 'SELECT_ANSWER': {
            const next = structuredClone(state);
            const map = buildQuestionMap(next);
            const question = map[action.questionKey];
            const target = findAnswer(question, action.answerUid);
            if (!question || !target) return state;

            if (question.isMultipleAnswer) {
                // Checkbox: toggle the target independently.
                if (target.isSelected) {
                    clearAnswerFields(target);
                } else {
                    target.isSelected = true;
                }
            } else {
                // Radio: select the target, deselect its siblings.
                for (const answer of question.answers) {
                    if (answer.uid === action.answerUid) {
                        answer.isSelected = true;
                    } else if (answer.isSelected) {
                        clearAnswerFields(answer);
                    }
                }
            }
            // Normalize: clear anything no longer visible (handles shared reveals).
            clearHiddenQuestions(next);
            return next;
        }

        case 'SET_COMMENT': {
            const next = structuredClone(state);
            const answer = findAnswer(buildQuestionMap(next)[action.questionKey], action.answerUid);
            if (!answer) return state;
            answer.comment = action.comment;
            return next;
        }

        case 'SET_QUANTITY': {
            const next = structuredClone(state);
            const answer = findAnswer(buildQuestionMap(next)[action.questionKey], action.answerUid);
            if (!answer) return state;
            answer.quantity = action.quantity;
            return next;
        }

        default:
            return state;
    }
};
